import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Check } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Field } from "./PropertyDetail";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", type: "inquiry" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: form.type === "callback" ? "callback" : "website" });
      setDone(true);
      toast.success(lang === "es" ? "¡Mensaje enviado!" : "Message sent!");
      setForm({ name: "", email: "", phone: "", message: "", type: "inquiry" });
    } catch { toast.error("Error"); } finally { setLoading(false); }
  };

  const info = [
    { icon: Phone, l: t("common.phone"), v: "+1 305 555 0100" },
    { icon: Mail, l: t("common.email"), v: "hello@estatelux.com" },
    { icon: MapPin, l: "Office", v: "1 Brickell Ave, Miami, FL" },
    { icon: Clock, l: "Hours", v: "Mon–Sat, 9am–7pm" },
  ];

  return (
    <div className="pt-24">
      <div className="border-b border-border bg-card/40">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
          <span className="label-caps text-primary">{t("nav.contact")}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-light mt-3">{lang === "es" ? "Hablemos" : "Let's talk"}</h1>
        </div>
      </div>
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          {done ? (
            <div className="border border-primary/40 bg-primary/5 p-10 text-center" data-testid="contact-success">
              <span className="w-14 h-14 rounded-full bg-primary text-primary-foreground grid place-items-center mx-auto mb-4"><Check size={28} /></span>
              <h3 className="font-serif text-2xl">{lang === "es" ? "¡Gracias!" : "Thank you!"}</h3>
              <p className="text-muted-foreground font-light mt-2">{lang === "es" ? "Te responderemos muy pronto." : "We'll get back to you soon."}</p>
              <button onClick={() => setDone(false)} className="mt-6 label-caps text-primary" data-testid="contact-again">{lang === "es" ? "Enviar otro" : "Send another"}</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="label-caps text-muted-foreground">{lang === "es" ? "Tipo de solicitud" : "Request type"}</label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-2 rounded-none" data-testid="contact-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inquiry">{lang === "es" ? "Consulta general" : "General inquiry"}</SelectItem>
                    <SelectItem value="callback">{lang === "es" ? "Solicitar llamada" : "Request callback"}</SelectItem>
                    <SelectItem value="submission">{lang === "es" ? "Publicar mi propiedad" : "Submit my property"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label={t("common.name")} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="contact-name" required />
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("common.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contact-email" required />
                <Field label={t("common.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="contact-phone" />
              </div>
              <div>
                <label className="label-caps text-muted-foreground">{t("common.message")}</label>
                <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} data-testid="contact-message"
                  className="w-full mt-2 bg-background border border-border focus:border-primary px-3 py-2.5 text-sm outline-none transition-colors resize-none" />
              </div>
              <button disabled={loading} className="w-full py-3.5 bg-primary text-primary-foreground label-caps disabled:opacity-50 hover:brightness-110 transition-[filter]" data-testid="contact-submit">{loading ? t("common.sending") : t("common.submit")}</button>
            </form>
          )}
        </div>
        <div className="space-y-8">
          <div className="grid grid-cols-2 gap-px bg-border border border-border">
            {info.map((it, i) => (
              <div key={i} className="bg-card p-6">
                <it.icon size={20} className="text-primary mb-3" strokeWidth={1.3} />
                <p className="label-caps text-muted-foreground text-[0.6rem]">{it.l}</p>
                <p className="mt-1 font-light">{it.v}</p>
              </div>
            ))}
          </div>
          <div className="aspect-video bg-card border border-border relative overflow-hidden">
            <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)", backgroundSize: "40px 40px", opacity: 0.4 }} />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
              <MapPin size={32} className="text-primary mx-auto" />
              <p className="label-caps mt-2">Brickell, Miami</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
